import {pickYyzArrivalPickup, pickYyzArrivalStart, resolveYyzTerminal} from '@/features/navigation/data/yyzArrivalEntries'
import {resolveStaticWalkPlan} from '@/features/navigation/data/yyzStaticWalkSteps'
import {resolveFlightType} from '@/features/smart-time/flightTimeService'

import domesticArrivals from '@img/airport-guide/domestic-arrivals-photo.jpg'
import internationalArrivals from '@img/airport-guide/international-arrivals-photo.jpg'
import t1DoorA from '@img/airport-guide/terminal1-door-a-photo.jpg'
import t3DomesticArrivals from '@img/airport-guide/t3-domestic-arrivals-photo.jpg'
import t3InternationalArrivals from '@img/airport-guide/t3-international-arrivals-photo.jpg'
import t3DoorA from '@img/airport-guide/t3-door-a-photo.jpg'

/**
 * THE WEB SIDE OF THE AIRPORT WALK — everything that adapts the handed-over mobile files
 * without editing them.
 *
 * ── ⚠️ NOTHING UNDER `data/` MAY BE TOUCHED ───────────────────────────────────────
 * Those files are byte-for-byte copies, and that is the point: there is no "passenger has
 * arrived" message anywhere in the system, so each client infers arrival by watching the
 * other's shared position against its OWN copy of the booth coordinate. A coordinate
 * edited here would not error. The three clients would simply disagree about when arrival
 * happened, silently, while all watching the same stream. So adaptations live in this file
 * and the originals stay pristine.
 */

/**
 * THE TERMINAL THE FLIGHT ITSELF LANDED AT — `arrival.terminal` on the provider record.
 *
 * ⚠️ THIS IS THE ANSWER, AND IT WAS BEING THROWN AWAY. The backend's flight record carries
 * the arrival stand ("3" for a Terminal 3 arrival); the walk was resolved from the
 * passenger's browser position alone and fell through to Terminal 1 whenever that position
 * was missing — which on this screen is ALWAYS, because it is resolved with a null point
 * on purpose. A Terminal 3 arrival was therefore walked through Terminal 1, from a
 * photograph of a door in the wrong building.
 *
 * ⚠️ PEARSON HAS ONLY 1 AND 3 for scheduled arrivals, and only those two have a recorded
 * walk. Anything else the provider prints — a letter, a pier, an empty string — is not a
 * terminal this can route, so it answers null rather than rounding to one that exists.
 */
const API_TERMINALS = {1: 'T1', 3: 'T3'}

export function terminalFromFlight(flight) {
    const raw = String(flight?.arrival?.terminal ?? '').trim().replace(/^T/i, '')
    return API_TERMINALS[raw] || null
}

/**
 * WHICH TERMINAL — measured, then declared, then the demo stand-in.
 *
 * ⚠️ THE ORDER IS THE POINT. A browser position inside a terminal is the passenger
 * physically standing there; the flight's arrival stand is what the airline published,
 * which is right almost always and stale occasionally. Where both answer, being in the
 * building wins.
 *
 * ⚠️ THE `T1` AT THE END IS STILL A LIE, and it is now the LAST thing tried rather than
 * the second. It exists because the portal is built and demonstrated nowhere near Toronto
 * (2026-09-10) and because every consumer below indexes into per-terminal tables that have
 * no null entry. `resolveWalkPlan` reports which of the three answered, as
 * `terminalSource`, so a screen can decline to name a terminal it is only guessing at.
 */
export function resolveTerminal(point, flight) {
    return resolveYyzTerminal(point) || terminalFromFlight(flight) || 'T1'
}

/** Which of the three sources above produced the terminal — 'position' | 'flight' | 'fallback'. */
function terminalSource(point, flight) {
    if (resolveYyzTerminal(point)) return 'position'
    if (terminalFromFlight(flight)) return 'flight'
    return 'fallback'
}

/**
 * DOMESTIC OR INTERNATIONAL, from whatever flight detail the trip carries.
 *
 * ⚠️ UNKNOWN RESOLVES TO INTERNATIONAL, and that bias is deliberate rather than a default
 * that happened to be first. The international walk is the longer one, because customs
 * sits well back from the kerb. Guessing it wrongly costs a longer walk; guessing the
 * other way sends someone to a door they cannot come out of.
 */
export function resolveScope(flight) {
    return resolveFlightType(flight)
}

/**
 * THE REFERENCE PHOTOGRAPHS — real pictures of the real doorways.
 *
 * ⚠️ A PASSENGER RECOGNISES A DOOR, NOT A LINE ON A MAP. That is the whole argument for
 * these existing: someone emerging from customs into an unfamiliar hall can match what is
 * in front of them against a photograph far faster than against a floor plan.
 *
 * ⚠️ ONE PICKUP DOOR PER TERMINAL, TWO STARTS. Door A is where both domestic and
 * international arrivals are collected; only the beginning of the walk differs by scope.
 */
const PHOTOS = {
    T1: {
        start: {Domestic: domesticArrivals, International: internationalArrivals},
        end: t1DoorA,
    },
    T3: {
        start: {Domestic: t3DomesticArrivals, International: t3InternationalArrivals},
        end: t3DoorA,
    },
}

const START_LABEL = {
    T1: {Domestic: 'Canada Arrivals', International: 'International Arrivals / Customs'},
    T3: {Domestic: 'Domestic Arrivals', International: 'International Arrivals'},
}

/**
 * WHAT EACH PHOTOGRAPH IS OF — the caption behind the thumbnail.
 *
 * ⚠️ IT DESCRIBES THE MOMENT, NOT THE PICTURE. "The arrivals board you'll see right
 * after landing" tells a passenger WHEN in their walk this is; "a photo of an arrivals
 * board" would tell them nothing they cannot see. Copied from the mobile screens verbatim
 * so a passenger who used the app last month reads the same words.
 */
const START_DESCRIPTION = {
    T1: {
        Domestic: "The arrivals board you’ll see right after landing on a domestic flight — your walk to pickup starts here.",
        International: "After clearing customs and collecting your bags, you’ll exit through the international arrivals area.",
    },
    T3: {
        Domestic: "The arrivals board you’ll see right after landing on a domestic flight at Terminal 3 — your walk to pickup starts here.",
        International: "After clearing customs and collecting your bags, you’ll exit through Terminal 3’s international arrivals area.",
    },
}

const END_DESCRIPTION = {
    T1: 'Look for the blue "A" sign and the Pre-Arranged Car & Limo Services desk — that’s where your driver will meet you.',
    T3: 'Look for the blue "A" sign at Terminal 3’s curb — that’s where your driver will meet you.',
}

/**
 * HOW LONG THE WALK TAKES, FOR SOMEONE CARRYING THEIR LUGGAGE.
 *
 * ⚠️ THE RECORDED `etaSeconds` ARE TOO FAST TO QUOTE, and they are not wrong — they are
 * the product owner's own timings, walked in person, unencumbered. Terminal 1's
 * international route totals 136 m in 80 seconds, which is 6 km/h: quicker than an average
 * walk and nothing like a passenger crossing a crowded arrivals hall behind a suitcase.
 * Shown as "about 1 min" it reads as a screen that has not understood the question.
 *
 * ⚠️ SO THE DISTANCE IS TRUSTED AND THE TIME IS DERIVED. The metres are surveyed and
 * exact; the pace is ours. The recorded time is still the floor, so this can never quote
 * FASTER than the walk was actually done — only slower, which is the safe direction when
 * someone is deciding whether they have time to stop for a coffee.
 *
 * ⚠️ AND IT ROUNDS UP. Rounding to nearest turns a 1.7 minute walk into "2 min" and a
 * 2.3 minute one into "2 min" as well; a passenger who is told two and takes three has
 * been misled by a rounding rule, which is a silly way to lose their trust.
 *
 * Nothing here edits the recorded data — see the note at the top of this file.
 */
const LUGGAGE_METRES_PER_MINUTE = 60

/**
 * Under this, a time is not the useful answer — "13 m" already says everything, and
 * "about 1 min" next to it invites the reader to wonder what the catch is.
 */
const TRIVIAL_WALK_METRES = 50

/**
 * The walk as one phrase: "136 m · about 3 min", or "13 m · under a minute".
 *
 * @param {{distanceMeters: number, etaSeconds: number}} totals from `yyzStaticWalkTotals`
 */
export function formatWalkSummary(totals) {
    const metres = Math.round(Number(totals?.distanceMeters) || 0)
    if (metres < TRIVIAL_WALK_METRES) return `${metres} m · under a minute`

    const recorded = (Number(totals?.etaSeconds) || 0) / 60
    const paced = metres / LUGGAGE_METRES_PER_MINUTE
    const minutes = Math.max(1, Math.ceil(Math.max(recorded, paced)))
    return `${metres} m · about ${minutes} min`
}

/**
 * Everything one screen needs about a walk: where it starts, where it ends, the recorded
 * steps, and the two photographs.
 *
 * @param {{lat: number, lng: number}|null} at  the passenger, or null before a fix
 * @param {object|null} flight  the trip's flight details, if any
 */
export function resolveWalkPlan(at, flight) {
    const point = at && Number.isFinite(at.lat) && Number.isFinite(at.lng) ? [at.lng, at.lat] : null
    const terminal = resolveTerminal(point, flight)
    const scope = resolveScope(flight)

    const {steps, arrivalHeadline} = resolveStaticWalkPlan(terminal, scope)

    return {
        terminal,
        /* Which source named it — 'position' | 'flight' | 'fallback'. A screen that prints
           the terminal in words needs this: "Terminal 1" is a statement of fact when the
           flight or the passenger's position said so, and a guess otherwise. */
        terminalSource: terminalSource(point, flight),
        scope,
        steps,
        arrivalHeadline,
        /* Null when there are no flight details \u2014 the mobile app then starts the walk from
           Carousel 1 on the baggage claim level, which is a real place rather than a
           guess. */
        start: pickYyzArrivalStart(flight),
        // Never null: with no flight it is the pre-arranged booth, which is what Hopn's own
        // airport guide already tells every passenger to use.
        pickup: pickYyzArrivalPickup(terminal, flight),
        startPhoto: PHOTOS[terminal].start[scope],
        startLabel: START_LABEL[terminal][scope],
        startDescription: START_DESCRIPTION[terminal][scope],
        endPhoto: PHOTOS[terminal].end,
        endLabel: 'Pickup Point — Door A',
        endDescription: END_DESCRIPTION[terminal],
    }
}

export default resolveWalkPlan
